import { createFileRoute } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import type { Icon } from '@phosphor-icons/react';
import {
  FloppyDisk,
  UserPlus,
  DotsThreeVertical,
  ArrowsClockwise,
  PencilSimple,
} from '@phosphor-icons/react';

type IconType = Icon;

const USERS = [
  { name: 'Darrel Brown', email: 'operations@kombatix.io', role: 'Admin', initials: 'DB' },
  { name: 'System Service', email: 'service@intellitryx.iam.gserviceaccount.com', role: 'Service', initials: 'SS' },
];

const AUDIT_LOG: { icon: IconType; description: string; user: string; time: string }[] = [
  { icon: ArrowsClockwise, description: 'Triggered manual normalization for peoplefinders', user: 'Darrel Brown', time: '15 min ago' },
  { icon: PencilSimple, description: 'Updated Chargebee field mapping: cf_custom_field', user: 'System (Auto)', time: '2 hours ago' },
  { icon: UserPlus, description: 'Added service account for BigQuery pipeline', user: 'Darrel Brown', time: '1 day ago' },
];

const ROLE_STYLES: Record<string, string> = {
  Admin: 'bg-primary/10 text-primary border-primary/20',
  Service: 'bg-blue-50 text-blue-700 border-blue-100',
  Viewer: 'bg-slate-100 text-slate-600 border-slate-200',
};

function SettingsPage() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">System Settings</h2>
        <p className="text-slate-500 text-sm mt-1">
          Manage platform configuration, user access, and review audit activity.
        </p>
      </div>

      {/* Platform Configuration */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Platform Configuration</h3>
        <p className="text-xs text-slate-400 mb-6">Core infrastructure settings for the IntelliTryx platform.</p>

        <div className="grid grid-cols-2 gap-6 max-w-2xl">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">GCP Project ID</label>
            <input
              type="text"
              defaultValue="intellitryx"
              readOnly
              className="w-full px-3 py-2 text-sm font-mono border border-slate-200 rounded-lg bg-slate-50 text-slate-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Region</label>
            <select
              defaultValue="us-central1"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700"
            >
              <option value="us-central1">us-central1</option>
              <option value="us-east1">us-east1</option>
              <option value="europe-west1">europe-west1</option>
            </select>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-slate-100">
          <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition">
            <FloppyDisk size={18} weight="regular" />
            Save Configuration
          </button>
        </div>
      </div>

      {/* User Management */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-bold text-slate-900">User Management</h3>
            <p className="text-xs text-slate-400 mt-0.5">Manage access to the IntelliTryx admin portal.</p>
          </div>
          <button className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition">
            <UserPlus size={16} weight="regular" />
            Invite User
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-6 py-3">User</th>
              <th className="text-left px-6 py-3">Email</th>
              <th className="text-left px-6 py-3">Role</th>
              <th className="text-center px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {USERS.map((user) => (
              <tr key={user.email} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition group">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                      {user.initials}
                    </div>
                    <span className="text-sm font-semibold text-slate-900">{user.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">{user.email}</td>
                <td className="px-6 py-4">
                  <span className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border',
                    ROLE_STYLES[user.role] || ROLE_STYLES.Viewer,
                  )}>
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <button className="text-slate-400 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition">
                    <DotsThreeVertical size={20} weight="regular" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Audit Log */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Audit Log</h3>
        <p className="text-xs text-slate-400 mb-5">Recent administrative actions and system events.</p>

        <div className="space-y-4">
          {AUDIT_LOG.map((entry, i) => {
            const Icon = entry.icon;
            return (
            <div key={i} className="flex items-start gap-4 pb-4 border-b border-slate-50 last:border-0 last:pb-0">
              <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                <Icon size={20} weight="regular" className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700">{entry.description}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-slate-400 font-medium">{entry.user}</span>
                  <span className="text-slate-300">|</span>
                  <span className="text-xs text-slate-400">{entry.time}</span>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsPage,
});
